import { useMemo } from "react";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * A-Z quick-jump index for the Glossary page. Mirrors the TOC pattern used by
 * blogs: pass in the currently-displayed terms so we know which letters have
 * content, and a `focusLetter` marker if we want to visually indicate where
 * the reader currently is. Clicking a letter smooth-scrolls to that letter's
 * section header via its `#gl-letter-A` id.
 *
 * `orientation="vertical"` renders as a tight left-rail TOC-style column
 * (used inside a sticky desktop aside); default is a horizontal chip grid
 * (used on mobile above the term list).
 */
export default function GlossaryAlphaIndex({ terms, focusLetter, orientation = "horizontal", label }) {
  const available = useMemo(() => {
    const s = new Set();
    for (const t of terms) {
      const c = (t.term?.[0] || "").toUpperCase();
      if (c) s.add(c);
    }
    return s;
  }, [terms]);

  const jump = (letter) => {
    const el = document.getElementById(`gl-letter-${letter}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (orientation === "vertical") {
    return (
      <nav
        data-testid="glossary-alpha-index-vertical"
        aria-label="Jump to letter"
        className="flex flex-col"
      >
        {label && (
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-3">{label}</div>
        )}
        <ol className="border-l border-slate-200 dark:border-slate-800">
          {LETTERS.map((L) => {
            const has = available.has(L);
            const isFocus = focusLetter === L;
            return (
              <li key={L}>
                <button
                  type="button"
                  data-testid={`glossary-alpha-${L}`}
                  disabled={!has}
                  onClick={() => has && jump(L)}
                  aria-current={isFocus ? "true" : undefined}
                  title={has ? `Jump to ${L}` : `No terms for ${L}`}
                  className={
                    `w-full text-left pl-3 py-1 text-[13px] font-heading transition-colors -ml-px border-l-2 ` +
                    (has
                      ? (isFocus
                          ? "border-sky-700 text-sky-800 dark:text-sky-300 font-semibold"
                          : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:border-slate-400")
                      : "border-transparent text-slate-300 dark:text-slate-700 cursor-default")
                  }
                >
                  {L}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return (
    <nav
      data-testid="glossary-alpha-index"
      aria-label="Jump to letter"
      className="flex flex-wrap gap-1 border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur rounded-md px-2 py-2"
    >
      {LETTERS.map((L) => {
        const has = available.has(L);
        const isFocus = focusLetter === L;
        return (
          <button
            key={L}
            type="button"
            data-testid={`glossary-alpha-${L}`}
            disabled={!has}
            onClick={() => has && jump(L)}
            className={
              `w-7 h-7 md:w-8 md:h-8 grid place-items-center rounded font-heading text-sm font-semibold transition-colors ` +
              (has
                ? (isFocus
                    ? "bg-sky-700 text-white"
                    : "text-slate-700 dark:text-slate-200 hover:bg-sky-100 dark:hover:bg-sky-900/40 hover:text-sky-800 dark:hover:text-sky-300")
                : "text-slate-300 dark:text-slate-700 cursor-default")
            }
            aria-current={isFocus ? "true" : undefined}
            title={has ? `Jump to ${L}` : `No terms for ${L}`}
          >
            {L}
          </button>
        );
      })}
    </nav>
  );
}
